import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableHead,
  Paper,
  Box,
  Typography,
  IconButton,
  Collapse,
  List,
  TableContainer,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Popover from "@mui/material/Popover";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import BlockIcon from "@mui/icons-material/Block";
import MessageIcon from "@mui/icons-material/Message";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import TextField from "@mui/material/TextField";
import SendIcon from "@mui/icons-material/Send";
import {
  HeaderRow,
  HeaderCell,
  DataRow,
  DataCell,
  TotalHoursBox,
  WeekSelector,
  HeaderBox,
  MainContainer,
  StyledMenu,
  DetailRow,
  DetailContent,
  DetailTable,
  CommentCell,
  getWeekDates,
  CommentsHistoryPopover,
  commentPopoverProps,
  closeButtonStyles,
} from "./TimesheetTableStyles";
import styled from "@emotion/styled";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  "& .MuiTable-root": {
    borderCollapse: "separate",
    borderSpacing: "0 2px",
    tableLayout: "fixed",
  },
  "& .MuiTableCell-root": {
    borderBottom: "none",
  },
  "& .day-cell": {
    padding: "1px",
    width: "56px",
    height: "30px",
    borderRadius: 0,
  },
}));

const TruncatedCell = ({ children, tooltip, width, sx = {} }) => {
  return (
    <Tooltip title={tooltip || children} arrow placement="top">
      <Box
        sx={{
          width: width || "100%",
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: "pointer",
          fontSize: "0.8rem",
          ...sx,
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

const TimesheetManagerReviewTable = ({
  data,
  projects,
  resources,
  selectedProject,
  selectedResource,
  onProjectChange,
  onResourceChange,
  fridays,
  selectedFriday,
  onFridayChange,
  onReject,
  onApprove,
  onError,
  onCommentSave,
  isResourceRole,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [weekDates, setWeekDates] = useState([]);
  const [activeComment, setActiveComment] = useState({
    resourceId: null,
    weeklyTimesheetId: null,
    comment: "",
  });
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [projectAnchorEl, setProjectAnchorEl] = useState(null);
  const [localData, setLocalData] = useState(data);
  const [commentHistoryAnchorEl, setCommentHistoryAnchorEl] = useState(null);
  const [activeCommentHistory, setActiveCommentHistory] = useState({});
  const [resourceAnchorEl, setResourceAnchorEl] = useState(null);
  console.log(localData);

  const formatTimeValue = (value) => {
    if (!value || value === "0") return "0";

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "0";

    const hours = Math.floor(numValue);
    const minutes = Math.round((numValue - hours) * 100);

    const additionalHours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    const totalHours = hours + additionalHours;

    if (remainingMinutes === 0) {
      return totalHours.toString();
    } else {
      return `${totalHours}.${remainingMinutes.toString().padStart(2, "0")}`;
    }
  };

  const calculateUniqueMilestoneBudgetHours = (entry) => {
    if (!entry.resource_weekly_timesheets || entry.resource_weekly_timesheets.length === 0) {
      return "0";
    }

    // Create a Map to store unique milestones with their budget hours
    const uniqueMilestones = new Map();

    entry.resource_weekly_timesheets.forEach((timesheet) => {
      const task_id = timesheet.task_id;
      const budgetHours = parseFloat(timesheet.resource_budget_hours || 0);

      // Only add if this milestone hasn't been seen before
      if (!uniqueMilestones.has(task_id) && budgetHours > 0) {
        uniqueMilestones.set(task_id, budgetHours);
      }
    });

    // Sum up the unique milestone budget hours
    const totalBudgetHours = Array.from(uniqueMilestones.values()).reduce(
      (sum, hours) => sum + hours,
      0
    );

    return totalBudgetHours.toString();
  };

  // Calculate total hours from daily hours array using the same logic as TimesheetTable. This treats decimal part as minutes (e.g., 0.25 = 25 minutes, not 0.25 hours)
  const calculateRowTotal = (dailyHoursArray) => {
    if (!dailyHoursArray || dailyHoursArray.length === 0) {
      return "0";
    }

    let totalMinutes = 0;
    dailyHoursArray.forEach((dayData) => {
      const value = Number(dayData.hours || dayData.logged_hours || 0);
      const hours = Math.floor(value);
      const minutes = Math.round((value - hours) * 100);
      totalMinutes += hours * 60 + minutes;
    });

    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;
    if (remainingMinutes === 0) {
      return totalHours.toString();
    } else {
      return `${totalHours}.${remainingMinutes.toString().padStart(2, "0")}`;
    }
  };
  
  useEffect(() => {
    if (selectedFriday?.value) {
      setWeekDates(getWeekDates(selectedFriday.value));
    }
  }, [selectedFriday?.value]);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  useEffect(() => {
    if (!selectedProject && projects?.length > 0) {
      const firstProject = {
        value: projects[0].project_id,
        label: projects[0].project_name,
      };
      onProjectChange(firstProject);
    }
  }, [projects, selectedProject, onProjectChange]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRejectClick = (resourceId) => {
    setConfirmationModal({
      open: true,
      title: "Reject Timesheet",
      message: "Are you sure you want to reject this timesheet?",
      onConfirm: () => {
        onReject(resourceId);
        setConfirmationModal(null);
      },
      colorCode: "#D32F2F",
    });
  };

  const handleApproveClick = (resourceId) => {
    setConfirmationModal({
      open: true,
      title: "Approve Timesheet",
      message: "Are you sure you want to approve this timesheet?",
      onConfirm: () => {
        onApprove(resourceId);
        setConfirmationModal(null);
      },
      colorCode: "#2E7D32",
    });
  };

  const toggleRowExpand = (resourceId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [resourceId]: !prev[resourceId],
    }));
  };

  const handleProjectSelect = (project) => {
    onProjectChange(project);
    handleProjectClose();
  };

  const projectOptions = projects || [];

  const handleProjectClick = (event) => {
    setProjectAnchorEl(event.currentTarget);
  };

  const handleProjectClose = () => {
    setProjectAnchorEl(null);
  };

  const handleCommentHistoryClick = (e, timesheet) => {
    e.stopPropagation();
    setCommentHistoryAnchorEl(e.currentTarget);
    setActiveComment({
      resourceId: timesheet.resource_id,
      weeklyTimesheetId: timesheet.weekly_timesheet_id,
      comment: "",
    });

    // Map and normalize comments with proper timestamps
    const managerComments = (timesheet.project_manager_comments || []).map(
      (comment) => ({
        ...comment,
        type: "manager",
        name: comment.resource_name,
        timestamp: comment.created_on,
        comment: comment.comment,
      })
    );

    const resourceComments = (timesheet.resource_comments || []).map(
      (comment) => ({
        ...comment,
        type: "resource",
        name: comment.resource_name,
        timestamp: comment.created_on,
        comment: comment.comment,
      })
    );

    setActiveCommentHistory({
      managerComments,
      resourceComments,
    });
  };

  const handleCommentHistoryClose = () => {
    setCommentHistoryAnchorEl(null);
    setActiveCommentHistory({});
  };

  const formatDate = (timestamp) => {
    const date = new Date(parseInt(timestamp));
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleResourceClick = (event) => {
    setResourceAnchorEl(event.currentTarget);
  };

  const handleResourceClose = () => {
    setResourceAnchorEl(null);
  };

  const handleResourceSelect = (resource) => {
    onResourceChange(resource);
    handleResourceClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCommentSend();
    }
  };

  const handleCommentSend = () => {
    if (!activeComment.comment.trim()) return;

    // Create a new comment object for UI display
    const newComment = {
      comment: activeComment.comment,
      timestamp: Date.now().toString(),
      created_on: new Date().toISOString(),
      type: "manager",
      name: "You", // This will be updated with actual name by the API
      weekly_timesheet_id: activeComment.weeklyTimesheetId,
    };

    // Add comment to history UI immediately for responsive feedback
    setActiveCommentHistory((prev) => ({
      ...prev,
      managerComments: [newComment, ...(prev.managerComments || [])],
    }));
    console.log("activeComment", activeComment);

    // Send the comment to the API through the viewmodel
    const success = onCommentSave(
      activeComment.resourceId,
      activeComment.weeklyTimesheetId,
      activeComment.comment
    );
    if (success) {
      // Clear the input on success
      setActiveComment((prev) => ({ ...prev, comment: "" }));
    }
  };

  return (
    <Box>
      <MainContainer>
        <HeaderBox>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box className="week-section">
              <Typography className="title">Week Ending</Typography>
              <WeekSelector>
                <Box
                  className="week-dropdown"
                  onClick={handleClick}
                  sx={{ cursor: "pointer" }}
                >
                  <Typography className="week-text">
                    {selectedFriday?.label || "Select Week"}
                  </Typography>
                  <KeyboardArrowDownIcon className="dropdown-arrow" />
                </Box>
              </WeekSelector>
            </Box>

            <Box className="week-section">
              <Typography className="title">
                {isResourceRole ? "Resource" : "Project"}
              </Typography>
              <WeekSelector>
                <Box
                  className="week-dropdown"
                  onClick={
                    isResourceRole ? handleResourceClick : handleProjectClick
                  }
                  sx={{ cursor: "pointer" }}
                >
                  <Typography className="week-text">
                    {isResourceRole
                      ? selectedResource?.label || "Select Resource"
                      : selectedProject?.label || "Select Project"}
                  </Typography>
                  <KeyboardArrowDownIcon className="dropdown-arrow" />
                </Box>
              </WeekSelector>
            </Box>
          </Box>
        </HeaderBox>

        <StyledTableContainer component={Paper}>
          <Table style={{ tableLayout: "fixed" }}>
            <TableHead>
              <HeaderRow>
                <HeaderCell style={{ width: "40px" }}></HeaderCell>
                <HeaderCell style={{ width: "210px" }}>MEMBER</HeaderCell>
                <HeaderCell style={{ width: "60px" }}>BUDGET HOURS</HeaderCell>
                {weekDates.map((dateInfo, index) => (
                  <HeaderCell
                    key={index}
                    className="date-cell"
                    style={{ width: "50px" }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        backgroundColor: "#5F80F6",
                        marginTop: "1px",
                        marginBottom: "1px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "relative",
                        padding: "2px",
                        ...(index === 0 && {
                          borderTopLeftRadius: "4px",
                          borderBottomLeftRadius: "4px",
                        }),
                        ...(index === 6 && {
                          borderTopRightRadius: "4px",
                          borderBottomRightRadius: "4px",
                        }),
                        "@keyframes pulseAnimation": {
                          "0%": {
                            transform: "scale(1)",
                            boxShadow: "0 0 0 0 rgba(211, 47, 47, 0.7)",
                          },
                          "50%": {
                            transform: "scale(1.1)",
                            boxShadow: "0 0 0 6px rgba(211, 47, 47, 0)",
                          },
                          "100%": {
                            transform: "scale(1)",
                            boxShadow: "0 0 0 0 rgba(211, 47, 47, 0)",
                          },
                        },
                        "@keyframes glowAnimation": {
                          "0%": {
                            opacity: 0.4,
                            backgroundColor: "#F44336",
                          },
                          "50%": {
                            opacity: 1,
                            backgroundColor: "#D32F2F",
                          },
                          "100%": {
                            opacity: 0.4,
                            backgroundColor: "#F44336",
                          },
                        },
                      }}
                    >
                      <span
                        className="day"
                        style={{
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          fontSize: "0.7rem",
                          lineHeight: "1",
                        }}
                      >
                        {dateInfo.day}
                      </span>
                      <span
                        className="date"
                        style={{
                          fontSize: "0.6rem",
                          lineHeight: "1",
                        }}
                      >
                        {dateInfo.date}
                      </span>
                    </Box>
                  </HeaderCell>
                ))}
                <HeaderCell style={{ width: "80px" }}>TOTAL HOURS</HeaderCell>
                <HeaderCell style={{ width: "100px" }}>APPROVAL</HeaderCell>
              </HeaderRow>
            </TableHead>
            <TableBody>
              {localData.map((entry) => (
                <React.Fragment key={entry.resource_id}>
                  <DataRow>
                    <DataCell style={{ width: "40px" }}>
                      <IconButton
                        size="small"
                        onClick={() => toggleRowExpand(entry.resource_id)}
                        sx={{
                          padding: "4px",
                          "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.04)",
                          },
                        }}
                      >
                        {expandedRows[entry.resource_id] ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </IconButton>
                    </DataCell>
                    <DataCell style={{ width: "210px" }}>
                      <TruncatedCell tooltip={entry.resource_name}>
                        {entry.resource_name}
                      </TruncatedCell>
                    </DataCell>
                    <DataCell className="budget-cell" style={{ width: "60px" }}>
                      <span className="budget-text">
                        {/* {formatTimeValue(entry.total_budget_hours)} */}
                        {formatTimeValue(calculateUniqueMilestoneBudgetHours(entry))}
                      </span>
                    </DataCell>
                    {weekDates.map((date, index) => {
                      const dayData = entry.total_weekly_hours.find(
                        (d) => d.timesheet_day === date.day
                      );
                      const isWeekend = date.day.toLowerCase().startsWith("s");
                      return (
                        <DataCell
                          key={index}
                          className="day-cell"
                          style={{ width: "50px" }}
                          sx={{
                            backgroundColor: isWeekend ? "#EBEBEB" : "#FFFFFF",
                            textAlign: "center",
                            padding: "0px",
                            borderSpacing: "0px",
                            borderCollapse: "separate",
                          }}
                        >
                          <TotalHoursBox
                            sx={{
                              backgroundColor: isWeekend
                                ? "#EBEBEB"
                                : "#FFFFFF",
                              border: isWeekend ? "none" : "1px solid #E0E0E0",
                              margin: "0px",
                              minHeight: "26px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.7rem",
                            }}
                          >
                            {formatTimeValue(dayData?.hours || "0")}
                          </TotalHoursBox>
                        </DataCell>
                      );
                    })}
                    <DataCell className="total-cell" style={{ width: "80px" }}>
                      <TotalHoursBox
                        sx={{
                          border:
                            Number(calculateRowTotal(entry.total_weekly_hours || [])) >
                            // Number(entry.total_budget_hours)
                            Number(calculateUniqueMilestoneBudgetHours(entry))
                              ? "2px solid #D32F2F"
                              : "none",
                        }}
                      >
                        {formatTimeValue(calculateRowTotal(entry.total_weekly_hours || []))}
                      </TotalHoursBox>
                    </DataCell>
                    <DataCell style={{ width: "100px" }}>
                      {entry.is_rejected ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                            color: "#D32F2F",
                          }}
                        >
                          <BlockIcon fontSize="small" />
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 500 }}
                          >
                            Rejected
                          </Typography>
                        </Box>
                      ) : entry.is_approved ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                            color: "#2E7D32",
                          }}
                        >
                          <CheckCircleOutlineIcon fontSize="small" />
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 500 }}
                          >
                            Approved
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          {(!entry.auto_approve || isResourceRole) && (
                            <Tooltip title="Approve" arrow>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleApproveClick(entry.resource_id)
                                }
                                sx={{
                                  color: "#2E7D32",
                                  padding: "4px",
                                  "&:hover": {
                                    backgroundColor: "rgba(46, 125, 50, 0.1)",
                                  },
                                }}
                              >
                                <CheckCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Reject" arrow>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleRejectClick(entry.resource_id)
                              }
                              sx={{
                                color: "#D32F2F",
                                padding: "4px",
                                "&:hover": {
                                  backgroundColor: "rgba(211, 47, 47, 0.1)",
                                },
                              }}
                            >
                              <CancelOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </DataCell>
                  </DataRow>

                  <DetailRow
                    sx={{
                      display: expandedRows[entry.resource_id]
                        ? "table-row"
                        : "none",
                    }}
                  >
                    <DataCell colSpan={12} sx={{ padding: 0, border: "none" }}>
                      <Collapse
                        in={expandedRows[entry.resource_id]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <DetailContent
                          sx={{
                            padding: 0,
                            margin: 0,
                            border: "1px solid #E0E0E0",
                            borderRadius: "0px",

                            overflow: "hidden",
                          }}
                        >
                          <DetailTable
                            style={{ tableLayout: "fixed" }}
                            sx={{
                              "& th:first-of-type": {
                                borderTopLeftRadius: "0px !important",
                                borderBottomLeftRadius: "0px !important",
                              },
                              "& th:last-child": {
                                borderTopRightRadius: "0px !important",
                                borderBottomRightRadius: "0px !important",
                              },
                            }}
                          >
                            <TableHead>
                              <HeaderRow>
                                {isResourceRole && (
                                  <HeaderCell
                                    style={{
                                      width: "80px",
                                      textAlign: "center",
                                      justifyContent: "center",
                                      alignItems: "center",
                                    }}
                                  >
                                    PROJECT
                                  </HeaderCell>
                                )}
                                <HeaderCell
                                  style={{
                                    width: isResourceRole ? "80px" : "160px",
                                    textAlign: "center",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  MILESTONE
                                </HeaderCell>
                                <HeaderCell
                                  style={{
                                    width: "90px",
                                    textAlign: "center",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  ACTIVITY
                                </HeaderCell>
                                <HeaderCell style={{ width: "60px" }}>
                                  BUDGET HOURS
                                </HeaderCell>
                                {weekDates.map((dateInfo, index) => (
                                  <HeaderCell
                                    key={index}
                                    className="date-cell"
                                    style={{ width: "50px" }}
                                  >
                                    <Box
                                      sx={{
                                        height: "100%",
                                        backgroundColor: "#5F80F6",
                                        marginTop: "1px",
                                        marginBottom: "1px",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        padding: "2px",
                                        ...(index === 0 && {
                                          borderTopLeftRadius: "4px",
                                          borderBottomLeftRadius: "4px",
                                        }),
                                        ...(index === 6 && {
                                          borderTopRightRadius: "4px",
                                          borderBottomRightRadius: "4px",
                                        }),
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      <span
                                        className="day"
                                        style={{
                                          fontSize: "0.65rem",
                                          lineHeight: "1",
                                        }}
                                      >
                                        {dateInfo.day}
                                      </span>
                                      <span
                                        className="date"
                                        style={{
                                          fontSize: "0.55rem",
                                          lineHeight: "1",
                                        }}
                                      >
                                        {dateInfo.date}
                                      </span>
                                    </Box>
                                  </HeaderCell>
                                ))}
                                                <HeaderCell style={{ width: "80px" }}>
                  TOTAL HOURS
                </HeaderCell>
                <HeaderCell style={{ width: "100px" }}>
                  MANAGER COMMENT
                </HeaderCell>
                              </HeaderRow>
                            </TableHead>
                            <TableBody>
                              {entry.resource_weekly_timesheets &&
                              entry.resource_weekly_timesheets.length > 0 ? (
                                entry.resource_weekly_timesheets.map(
                                  (timesheet) => (
                                    <DataRow
                                      key={
                                        timesheet.weekly_timesheet_id ||
                                        `timesheet-${Math.random()}`
                                      }
                                    >
                                      {isResourceRole && (
                                        <DataCell
                                          style={{
                                            width: "80px",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            justifyItems: "center",
                                            backgroundColor: "#F5F5F5",
                                          }}
                                        >
                                          <TruncatedCell width="80px">
                                            {timesheet.project_name || "N/A"}
                                          </TruncatedCell>
                                        </DataCell>
                                      )}
                                      <DataCell
                                        style={{
                                          width: isResourceRole
                                            ? "80px"
                                            : "160px",
                                          justifyContent: "center",
                                          alignItems: "center",
                                          justifyItems: "center",
                                          backgroundColor: "#F5F5F5",
                                        }}
                                      >
                                        <TruncatedCell
                                          width={
                                            isResourceRole ? "80px" : "160px"
                                          }
                                        >
                                          {timesheet.task_name || "Task"}
                                        </TruncatedCell>
                                      </DataCell>
                                      <DataCell
                                        style={{
                                          width: "90px",
                                          justifyContent: "center",
                                          alignItems: "center",
                                          justifyItems: "center",
                                          backgroundColor: "#F5F5F5",
                                        }}
                                      >
                                        <TruncatedCell width="90px">
                                          {(() => {
                                            // Look for activity in any timesheet entry that has activity data
                                            const timesheetWithActivity =
                                              timesheet.resource_timesheets?.find(
                                                (ts) =>
                                                  ts.activity?.activity_name
                                              );
                                            const activityName =
                                              timesheetWithActivity?.activity
                                                ?.activity_name;

                                            // If no activity name found, check if there's a milestone_activity_mapping_id
                                            if (
                                              !activityName &&
                                              timesheet.milestone_activity_mapping_id
                                            ) {
                                              return "Activity";
                                            }

                                            return activityName || "N/A";
                                          })()}
                                        </TruncatedCell>
                                      </DataCell>
                                      <DataCell
                                        className="budget-cell"
                                        style={{
                                          width: "60px",
                                          backgroundColor: "#F5F5F5",
                                        }}
                                      >
                                        <span
                                          className="budget-text"
                                          style={{
                                            fontSize: "0.8rem",
                                            backgroundColor: "#FFFFFF",
                                          }}
                                        >
                                          {formatTimeValue(
                                            timesheet.resource_budget_hours ||
                                              "0"
                                          )}
                                        </span>
                                      </DataCell>
                                      {weekDates.map((date, index) => {
                                        const dayData =
                                          timesheet.resource_timesheets?.find(
                                            (d) => d.timesheet_day === date.day
                                          );
                                        const isWeekend = date.day
                                          .toLowerCase()
                                          .startsWith("s");
                                        return (
                                          <DataCell
                                            key={index}
                                            style={{
                                              width: "50px",
                                              height: "26px",
                                              backgroundColor: "#F5F5F5",
                                            }}
                                            className="day-cell"
                                            sx={{
                                              textAlign: "center",
                                              padding: "0px",
                                              borderSpacing: "0px",
                                              borderCollapse: "separate",
                                              borderRadius: 0,
                                            }}
                                          >
                                            <TotalHoursBox
                                              style={{
                                                backgroundColor: isWeekend
                                                  ? "#EBEBEB"
                                                  : "#FFFFFF",
                                                border: isWeekend
                                                  ? "none"
                                                  : "1px solid #E0E0E0",
                                                margin: "0px",
                                                minHeight: "26px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "0.7rem",
                                              }}
                                            >
                                              {formatTimeValue(
                                                dayData?.logged_hours || "0"
                                              )}
                                            </TotalHoursBox>
                                          </DataCell>
                                        );
                                      })}
                                      <DataCell
                                        className="total-cell"
                                        style={{
                                          width: "80px",
                                          backgroundColor: "#F5F5F5",
                                        }}
                                      >
                                        <TotalHoursBox
                                          style={{
                                            backgroundColor: "#FFFFFF",
                                            marginBottom: "1px",
                                            border:
                                              Number(
                                                calculateRowTotal(timesheet.resource_timesheets || [])
                                              ) >
                                              Number(
                                                timesheet.resource_budget_hours
                                              )
                                                ? "2px solid #D32F2F"
                                                : "none",
                                          }}
                                        >
                                          {formatTimeValue(
                                            calculateRowTotal(timesheet.resource_timesheets || [])
                                          )}
                                        </TotalHoursBox>
                                      </DataCell>
                                      <DataCell
                                        style={{
                                          width: "100px",
                                          backgroundColor: "#F5F5F5",
                                          justifyItems: "center",
                                        }}
                                      >
                                        <CommentCell>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              backgroundColor: "#FFFFFF",
                                              gap: "4px",
                                              borderRadius: "4px",
                                              padding: "4px",
                                            }}
                                          >
                                            <Tooltip
                                              title="View Comments"
                                              placement="top"
                                              arrow
                                            >
                                              <div
                                                className="comment-icon-wrapper"
                                                onClick={(e) =>
                                                  handleCommentHistoryClick(
                                                    e,
                                                    timesheet
                                                  )
                                                }
                                                style={{ cursor: "pointer" }}
                                              >
                                                {timesheet.resource_comments &&
                                                timesheet.resource_comments
                                                  .length > 0 ? (
                                                  <MessageIcon
                                                    className="comment-icon has-comment"
                                                    fontSize="small"
                                                    sx={{ color: "#4CAF50" }}
                                                  />
                                                ) : timesheet.project_manager_comments &&
                                                  timesheet
                                                    .project_manager_comments
                                                    .length > 0 ? (
                                                  <MessageIcon
                                                    className="comment-icon has-comment"
                                                    fontSize="small"
                                                    sx={{ color: "#5F80F6" }}
                                                  />
                                                ) : (
                                                  <ChatBubbleOutlineIcon
                                                    className="comment-icon"
                                                    fontSize="small"
                                                    sx={{ color: "#999" }}
                                                  />
                                                )}
                                              </div>
                                            </Tooltip>
                                          </Box>
                                        </CommentCell>
                                      </DataCell>
                                    </DataRow>
                                  )
                                )
                              ) : (
                                <DataRow>
                                  <DataCell
                                    colSpan={
                                      isResourceRole
                                        ? 10 + weekDates.length
                                        : 9 + weekDates.length
                                    }
                                    sx={{ textAlign: "center" }}
                                  >
                                    No timesheet data found
                                  </DataCell>
                                </DataRow>
                              )}
                            </TableBody>
                          </DetailTable>
                        </DetailContent>
                      </Collapse>
                    </DataCell>
                  </DetailRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </MainContainer>

      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {fridays.map((friday) => (
          <MenuItem
            key={friday.value}
            onClick={() => onFridayChange(friday)}
            selected={friday.value === selectedFriday?.value}
          >
            {friday.label}
          </MenuItem>
        ))}
      </StyledMenu>

      {isResourceRole ? (
        <StyledMenu
          anchorEl={resourceAnchorEl}
          open={Boolean(resourceAnchorEl)}
          onClose={handleResourceClose}
        >
          {resources.map((resource) => (
            <MenuItem
              key={resource.value}
              onClick={() => handleResourceSelect(resource)}
              selected={resource.value === selectedResource?.value}
            >
              {resource.label}
            </MenuItem>
          ))}
        </StyledMenu>
      ) : (
        <StyledMenu
          anchorEl={projectAnchorEl}
          open={Boolean(projectAnchorEl)}
          onClose={handleProjectClose}
        >
          {projects.map((project) => (
            <MenuItem
              key={project.value}
              onClick={() => handleProjectSelect(project)}
              selected={project.value === selectedProject?.value}
            >
              {project.label}
            </MenuItem>
          ))}
        </StyledMenu>
      )}

      <Popover
        open={Boolean(commentHistoryAnchorEl)}
        anchorEl={commentHistoryAnchorEl}
        onClose={handleCommentHistoryClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        PaperProps={commentPopoverProps}
      >
        <CommentsHistoryPopover>
          <div className="comments-header">
            <Typography variant="h6">
              <MessageIcon className="icon" />
              Comments
            </Typography>
            <IconButton
              size="small"
              onClick={handleCommentHistoryClose}
              sx={closeButtonStyles}
            >
              <CloseIcon sx={{ fontSize: "12px" }} />
            </IconButton>
          </div>
          <List className="comment-list" disablePadding>
            {!activeCommentHistory?.managerComments?.length &&
            !activeCommentHistory?.resourceComments?.length ? (
              <div className="no-comments">
                <ChatBubbleOutlineIcon className="icon" />
                <Typography className="text">
                  No comments available for this timesheet entry
                </Typography>
              </div>
            ) : (
              [
                ...(activeCommentHistory?.managerComments || []),
                ...(activeCommentHistory?.resourceComments || []),
              ]
                .sort((a, b) => parseInt(a.timestamp) - parseInt(b.timestamp))
                .map((comment, index) => (
                  <div
                    key={`${comment.type}-${comment.timestamp}-${index}`}
                    className={`comment-bubble ${
                      comment.type === "resource" ? "manager" : "resource"
                    }`}
                  >
                    <Typography className="comment-text">
                      {comment.comment}
                    </Typography>
                    <div className="comment-meta">
                      <span className={`user-name ${comment.type}-name`}>
                        {comment.type === "manager" ? (
                          <SupervisorAccountIcon className="user-icon" />
                        ) : (
                          <ChatIcon className="user-icon" />
                        )}
                        {comment.type === "manager" ? "You" : comment.name}
                      </span>
                      <span className="comment-date">
                        <AccessTimeIcon className="time-icon" />
                        {formatDate(comment.timestamp)}
                      </span>
                    </div>
                  </div>
                ))
            )}
          </List>
          <div className="comment-input-container">
            <TextField
              className="comment-input"
              multiline
              maxRows={4}
              placeholder="Type a comment..."
              value={activeComment.comment}
              onChange={(e) =>
                setActiveComment((prev) => ({
                  ...prev,
                  comment: e.target.value,
                }))
              }
              onKeyPress={handleKeyPress}
              variant="outlined"
              fullWidth
              disabled={(() => {
                const entry = localData.find(
                  (entry) => entry.resource_id === activeComment.resourceId
                );
                return entry?.is_rejected || entry?.is_approved;
              })()}
            />
            <IconButton
              className="send-button"
              onClick={handleCommentSend}
              disabled={
                !activeComment.comment.trim() ||
                (() => {
                  const entry = localData.find(
                    (entry) => entry.resource_id === activeComment.resourceId
                  );
                  return entry?.is_rejected || entry?.is_approved;
                })()
              }
            >
              <SendIcon
                sx={{
                  fontSize: "16px",
                  color: "#FFFFFF",
                  transform: "rotate(-45deg)",
                }}
              />
            </IconButton>
          </div>
        </CommentsHistoryPopover>
      </Popover>

      {confirmationModal && (
        <ConfirmationModal
          open={confirmationModal.open}
          title={confirmationModal.title}
          message={confirmationModal.message}
          onClose={() => setConfirmationModal(null)}
          onConfirm={confirmationModal.onConfirm}
          buttonText={confirmationModal.title.split(" ")[0]}
          colorCode={confirmationModal.colorCode || "#2E7D32"}
        />
      )}
    </Box>
  );
};

export default React.memo(TimesheetManagerReviewTable);
